<template>
  <div role="listbox" ref="dropdown" :style="{width}" :class="[direction === 'up' ? `${baseCss}-direction-up` : null, dense ? `${baseCss}-dense` : null, 'inline-block', 'relative', dense ? 'text-xs' : 'text-sm', 'align-middle', disabled ? ['disabled', 'pointer-events-none'] : null, baseCss, hidden ? 'bcq-dropdown-hidden' : 'bcq-dropdown-visible', 'select-none']">
    <container @click.native="toggle" :class="['bcq-dropdown-header', 'cursor-pointer']" :title="headerLabel">
      <text-field-label v-if="label" :color="labelColor" :color-lightness="labelColorLightness" :text="label" :required="required" />
      <table-layout equal-cells tag="span" y-align="middle">
        <row>
          <cell x-align="center" v-if="icon" :class="[dense ? null : 'py-2', 'bcq-dropdown-header-icon-wrapper']">
            <icon :provider="iconProvider" :color="!hidden || touched ? color : 'black'" :color-lightness="!hidden || touched ? colorLightness : null" :name="icon" />
          </cell>
          <cell x-align="left" :class="[dense ? null : 'py-2', 'bcq-dropdown-header-text-wrapper', dense ? 'px-1' : 'px-2']" full-width>
            <div :class="['truncate', dense ? 'text-xs' : 'text-sm', !hidden || touched ? internalTextColor : $dark ? 'text-white' : 'text-black']" v-html="headerLabel" />
          </cell>
          <cell x-align="center" :class="[dense ? null : 'py-2', 'bcq-dropdown-header-icon-wrapper']">
            <icon :provider="iconProvider" :color="!hidden || touched ? color : 'black'" :color-lightness="!hidden || touched ? colorLightness : null" :name="hidden ? 'chevron-down' : 'chevron-up'" />
          </cell>
        </row>
      </table-layout>
      <line-ripple :visible="!hidden || touched" :bg-color="rippleColor" :bg-color-lightness="rippleColorLightness" :ripple-bg-color="color" :ripple-bg-color-lightness="colorLightness" />
      <text-field-label v-if="hint" align="right" :text="hint" :color="hintColor" :color-lightness="hintColorLightness" />
      <text-field-label v-if="error" :text="error" align="right" :color="errorColor" :color-lightness="errorColorLightness" />
    </container>
    <transition name="fade">
      <card v-if="!hidden && items.length" :elevation="elevation" :class="[hidden ? ['clip-0', 'opacity-0'] : null, 'bcq-dropdown-items', 'transition-all', 'duration-500', 'absolute', 'left-0', 'w-full']" :style="{zIndex: itemsZIndex}">
        <card-text :class="['cq-dropdown-items-container', 'overflow-auto']">
<!--          <collection-cluster :items="items">-->
<!--            <template v-slot:item="{ cell, item }">-->
<!--              <div :key="cell.index">{{ item.value }} / {{ cell.index }}</div>-->
<!--            </template>-->
<!--          </collection-cluster>-->
          <text-field v-if="searchable" autofocus clearable v-model="search" :placeholder="searchLabel" :color="color" :color-lightness="colorLightness" :class="['mb-1', 'bcq-dropdown-items-filter']" />
          <div :class="['overflow-auto']" :style="{maxHeight: itemsMaxHeight}">
            <table-layout y-align="middle">
              <dropdown-item
                @hide="hidden = true"
                @input="onInput($event)"
                v-for="(item, index) in filtered"
                ref="dropdown-item"
                :name="name"
                :class="item.hidden ? 'bcq-dropdown-item-hidden' : null"
                :dense="dense"
                :color="color"
                :color-lightness="colorLightness"
                :tag="item.tag"
                :to="item.to"
                :href="item.href"
                :target="item.target"
                :multiple="multiple"
                :disabled="item.disabled"
                :icon-left="item.iconLeft"
                :icon-right="item.iconRight"
                :text="item.text"
                :value="item.value"
                :index="index"
                :key="index" />
            </table-layout>
          </div>
        </card-text>
      </card>
    </transition>
  </div>
</template>
<script>
import methods from '../../mixins/methods'
import computed from '../../mixins/computed'
import props from '../../mixins/props'
import TextFieldLabel from '../text-field-label/text-field-label'
import DropdownItem from './dropdown-item'
// import CollectionCluster from 'vue-collection-cluster'

export default {
  name: 'Dropdown',
  components: {
    // CollectionCluster,
    DropdownItem,
    TextFieldLabel
  },
  mixins: [
    props,
    methods,
    computed
  ],
  data () {
    return {
      hidden: true,
      search: '',
      selection: [],
      headerLabel: '',
      baseCss: this.componentBaseCss('dropdown')
    }
  },
  watch: {
    checked () {
      this.createHeaderLabel(false)
    },
    hidden () {
      if (!this.hidden) {
        this.$nextTick(() => {
          this.preselect()
          if (this.searchable) {
            this.$el.querySelector('.bcq-dropdown-items-filter input').focus()
            this.search = ''
          }
        })
      }
      this.$emit('dropdown-toggle', {
        hidden: !!this.hidden,
        name: this.name
      })
    }
  },
  computed: {
    touched () {
      return !!this.checked.length
    },
    filtered () {
      if (this.searchable) {
        const search = this.search.toLowerCase()
        return this.items.filter(item => {
          if (item) {
            const text = item.text.toLowerCase()
            if (this.fuzzySearch ? text.indexOf(search) > -1 : text.substring(0, search.length) === search) {
              item.hidden = false
            } else {
              item.hidden = true
            }
            return item
          }
        })
      } else {
        return this.items
      }
    }
  },
  model: {
    prop: 'checked',
    event: 'input'
  },
  props: {
    searchable: {
      type: Boolean,
      default: false
    },
    searchLabel: {
      type: String,
      default: 'Filter items...'
    },
    fuzzySearch: {
      type: Boolean,
      default: false
    },
    dense: {
      type: Boolean
    },
    direction: {
      type: String,
      default: 'down',
      validator: value => {
        return [
          'down',
          'up'
        ].includes(value)
      }
    },
    iconProvider: {
      type: String,
      default: 'mdi'
    },
    rippleColor: {
      type: String
    },
    rippleColorLightness: {
      type: Number
    },
    rippleBgColor: {
      type: String
    },
    rippleBgColorLightness: {
      type: Number
    },
    checked: {
      type: Array,
      default: () => {
        return []
      }
    },
    items: {
      type: Array,
      default: () => {
        return []
      }
    },
    width: {
      type: String
    },
    name: {
      type: String
    },
    errorColor: {
      type: String,
      default: 'red'
    },
    errorColorLightness: {
      type: [Number, String],
      default: 500
    },
    color: {
      type: String,
      default: 'black'
    },
    itemsMaxHeight: {
      type: String,
      default: '247px'
    },
    itemsZIndex: {
      type: Number,
      default: 900
    },
    required: {
      type: Boolean,
      default: false
    },
    icon: {
      type: String
    },
    label: {
      type: String
    },
    error: {
      type: String
    },
    hint: {
      type: String
    },
    hintColor: {
      type: String
    },
    hintColorLightness: {
      type: [Number, String]
    },
    disabled: {
      type: Boolean,
      default: false
    },
    multiple: {
      type: Boolean,
      default: false
    },
    labelColor: {
      type: String
    },
    labelColorLightness: {
      type: [String, Number]
    }
  },
  created () {
    document.addEventListener('click', this.documentClick)
  },
  destroyed () {
    document.removeEventListener('click', this.documentClick)
  },
  mounted () {
    this.onMounted()
  },
  methods: {
    onMounted () {
      this.createHeaderLabel(true)
    },
    preselect () {
      const items = this.$refs['dropdown-item']
      if (items) {
        items.forEach(item => {
          if (this.checked.includes(item.value)) {
            item.checked = true
          }
        })
      }
    },
    createHeaderLabel (preselect) {
      let labels = []
      this.items.forEach(item => {
        if (this.checked.includes(item.value)) {
          labels.push(item.text)
        }
      })
      this.headerLabel = labels.length ? labels.join(', ') : this.label
      if (preselect) {
        this.preselect()
      }
    },
    toggle () {
      this.hidden = !this.hidden
    },
    documentClick (e) {
      let el = this.$refs['dropdown']
      let target = e.target
      if (el !== target && !el.contains(target)) {
        this.hidden = true
      }
    },
    onInput ($event) {
      this.selection = []
      if (!this.multiple) {
        this.selection.push($event.value)
      } else {
        this.selection = []
        const checked = []
        this.$refs['dropdown-item'].forEach(item => {
          if (item.checked) {
            checked.push(item.value)
          }
        })
        this.selection = checked
      }
      this.$emit('input', this.selection)
      this.$emit('change', this.selection)
    }
  }
}
</script>
<style>
.bcq-dropdown .bcq-dropdown-header-icon-wrapper,
.bcq-dropdown-item .bcq-dropdown-item-icon-wrapper
{
  width: 32px;
}

.bcq-dropdown,
.bcq-dropdown a
{
  color: rgba(0, 0, 0, .87);
  text-decoration: none;
}

.bcq-ui-theme-dark .bcq-dropdown .bcq-dropdown-items a
{
  color: #fff;
}

.bcq-dropdown .bcq-dropdown-item-label-wrapper,
.bcq-dropdown .bcq-dropdown-header-text-wrapper
{
  line-height: 32px;
}

.bcq-dropdown .bcq-dropdown-items
{
  margin-top: 3px;
}

.bcq-dropdown .bcq-dropdown-item-checked,
.bcq-dropdown .bcq-dropdown-item:hover
{
  background: var(--router-link-active);
}

.bcq-ui-theme-dark .bcq-dropdown .bcq-dropdown-item-checked,
.bcq-ui-theme-dark .bcq-dropdown .bcq-dropdown-item:hover
{
  background: var(--router-link-active-dark);
}

.bcq-dropdown:not(.bcq-dropdown-dense) .bcq-table-layout
{
  height: 40px;
}

.bcq-dropdown.bcq-dropdown-dense .bcq-table-layout
{
  height: 25px;
}

.bcq-dropdown.bcq-dropdown-direction-up .bcq-dropdown-items
{
  bottom: 38px;
}

.bcq-dropdown.bcq-dropdown-dense.bcq-dropdown-direction-up .bcq-dropdown-items
{
  bottom: 32px;
}

.bcq-dropdown .bcq-dropdown-item-hidden
{
  display: none !important;
}
</style>



// WEBPACK FOOTER //
// src/bcq-ui/components/dropdown/dropdown.vue